"use client";

import React from "react";
import { CalendarClock, CircleCheck as CheckCircle2, ClipboardCheck, Compass, FileText, House as Home, Layers, Sparkles, TrendingUp, UtensilsCrossed, Users, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface TimelineFactor {
  step: string;
  title: string;
  duration: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
interface LifeTrigger {
  title: string;
  subtitle: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const timelineFactors: TimelineFactor[] = [{
  step: "01",
  title: "Design finalization",
  duration: "Weeks 1–2",
  description: "Detailed 3D layout planning, structural integration with your home, and architectural elevations tailored to your lot.",
  icon: Compass
}, {
  step: "02",
  title: "Material selection",
  duration: "Weeks 2–3",
  description: "Specifying weather-resistant hardscapes, composite decking, natural stone veneers, outdoor-rated appliances, and lighting fixtures.",
  icon: Layers
}, {
  step: "03",
  title: "City of Brookhaven permitting for permanent structures",
  duration: "Weeks 3–6",
  description: "Submitting zoning checks, setback verifications, tree preservation plans, and structural drawings to Brookhaven community development.",
  icon: FileText
}, {
  step: "04",
  title: "Inspections",
  duration: "Construction phase",
  description: "Scheduled footing, framing, rough electrical, plumbing, and final certificate of completion inspections by city officials.",
  icon: ClipboardCheck
}];
const lifeTriggers: LifeTrigger[] = [{
  title: "Want more entertaining space",
  subtitle: "Seamless indoor-to-outdoor flow",
  description: "Your living room and kitchen feel crowded during weekend gatherings. An open-air pavilion or covered deck doubles your hosting capacity.",
  icon: Users
}, {
  title: "Preparing to sell",
  subtitle: "High buyer appeal in Metro Atlanta",
  description: "Neighborhood comps demand top-tier outdoor spaces. Move-in ready covered verandas and patios accelerate sales and offer strong returns.",
  icon: TrendingUp
}, {
  title: "Maximizing Brookhaven lot value",
  subtitle: "Transforming sloped or unused yard space",
  description: "Convert underutilized backyard topography into multi-tier retaining stone terraces, fire lounges, and private garden sanctuaries.",
  icon: Home
}, {
  title: "Want outdoor kitchen & dining",
  subtitle: "Year-round Southern hospitality",
  description: "Equip your patio with built-in gas grills, smoker alcoves, prep counters, beverage refrigerators, and overhead infrared heating.",
  icon: UtensilsCrossed
}];
export default function OutdoorLivingTimeline() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="outdoorlivingtimelineandtriggers" data-nav="light" ref={sectionRef} className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
        
        {/* Subsection 1: Timeline Factors */}
        <div>
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-semibold tracking-wide uppercase mb-4">
              <CalendarClock className="size-3.5" />
              Project Milestones
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground leading-tight">
              Outdoor living timeline factors
            </h2>
            <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed font-default">
              Understanding the critical phases ensures your covered porch, deck expansion, or patio transformation finishes on schedule without surprises.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Timeline Cards */}
            <div className="lg:col-span-7 space-y-4">
              {timelineFactors.map((factor, index) => {
              const IconComponent = factor.icon;
              return <Card key={factor.step} data-index={index} className="border border-border/80 bg-card shadow-xs hover:shadow-md transition-shadow duration-300 rounded-lg">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="flex items-center justify-between sm:justify-start gap-3 shrink-0">
                          <span className="font-mono text-sm font-bold text-primary px-2.5 py-1 rounded bg-primary/10">
                            {factor.step}
                          </span>
                          <div className="p-2.5 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center">
                            <IconComponent className="size-4" />
                          </div>
                        </div>

                        <div className="flex-1 space-y-1.5">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-lg font-semibold font-serif text-card-foreground">
                              {factor.title}
                            </h3>
                            <Badge variant="outline" className="text-xs font-normal border-border">
                              {factor.duration}
                            </Badge>
                          </div>
                          <p className="text-sm text-foreground/75 leading-relaxed font-default">
                            {factor.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Contextual Image & Permit Advisory */}
            <div className="lg:col-span-5 space-y-6">
              <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-sm aspect-4/3">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_covered_porch_deck.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_covered_porch_deck.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_covered_porch_deck.png-wvc-sizes" alt="Craftsman covered back porch with tongue and groove cedar ceiling and stone outdoor fireplace in Brookhaven" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent flex items-end p-6">
                  <div className="text-secondary-foreground space-y-1">
                    <p className="text-xs uppercase tracking-wider font-semibold text-primary">
                      Permitted Construction
                    </p>
                    <p className="text-sm font-medium">
                      Built to City of Brookhaven building codes and zoning ordinances.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/60 border border-border rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2.5 text-foreground font-semibold text-sm">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Brookhaven Permitting Coordination</span>
                </div>
                <p className="text-xs text-foreground/80 leading-relaxed font-default">
                  We handle every structural review, storm water variance, and inspection schedule directly with Brookhaven City Hall to avoid construction stalls.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-border" />

        {/* Subsection 2: Life-Moment Triggers */}
        <div>
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-foreground text-xs font-semibold tracking-wide uppercase mb-4">
              <Sparkles className="size-3.5 text-primary" />
              Lifestyle Assessment
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground leading-tight">
              Is it time to add outdoor living space?
            </h2>
            <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed font-default">
              Recognize the life-moment triggers that signal your household is ready for a dedicated outdoor kitchen, deck, or screened living addition.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Trigger Visual Showcase */}
            <div className="lg:col-span-5 order-2 lg:order-1 space-y-6">
              <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-sm aspect-4/3">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_patio_kitchen.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_patio_kitchen.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_patio_kitchen.png-wvc-sizes" alt="Custom outdoor kitchen with stainless grill, granite prep counters, and dining patio in a Brookhaven residential backyard" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/85 via-secondary/20 to-transparent flex items-end p-6">
                  <div className="text-secondary-foreground space-y-1">
                    <p className="text-xs uppercase tracking-wider font-semibold text-primary">
                      Lifestyle Upgrade
                    </p>
                    <p className="text-sm font-medium">
                      Designed for year-round entertaining, dining, and relaxation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-card border border-border shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-serif font-semibold text-card-foreground">
                    Ready to evaluate your backyard?
                  </h3>
                  <p className="text-xs text-foreground/75 mt-0.5 font-default">
                    Schedule an on-site consultation with our renovation team.
                  </p>
                </div>
                <Button asChild variant="default" className="shrink-0">
                  <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-2">
                    Request an estimate
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Trigger Cards */}
            <div className="lg:col-span-7 order-1 lg:order-2 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {lifeTriggers.map((trigger, idx) => {
              const TriggerIcon = trigger.icon;
              return <Card key={trigger.title} data-index={idx} className="border border-border/80 bg-card hover:border-primary/60 transition-colors duration-300 rounded-lg">
                    <CardContent className="p-6 space-y-3.5">
                      <div className="size-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center">
                        <TriggerIcon className="size-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold font-serif text-card-foreground leading-snug">
                          {trigger.title}
                        </h3>
                        <p className="text-xs font-medium text-primary mt-0.5">
                          {trigger.subtitle}
                        </p>
                      </div>
                      <p className="text-xs sm:text-sm text-foreground/75 leading-relaxed font-default">
                        {trigger.description}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>
        </div>

      </div>
    </section>;
}